"use client";
import Image from "next/image";
import { m, useInView } from "framer-motion";
import { useRef } from "react";
import { useAppContext } from "../appContext";
import Button from "../button";
import CalendlyButton from "../calendlyButton";
import { useTranslations } from "next-intl";
import { variantsBlock, variantsParent, variantsText } from "../variants";

import type { HeroButton } from "@/types/sections";
import type { ButtonVariant } from "../button";

const defaultButtons: HeroButton[] = [
  { type: "lightbox", action: "cta", variant: "default", label: "calculateQuote" },
  { type: "calendly", variant: "outline" },
];

interface Props {
  data: any;
  isHero?: boolean;
  title?: string;
}

function getHeadlineIconAlt(item: any, isMainHero?: boolean) {
  if (typeof item?.alt === "string" && item.alt.trim().length > 0) {
    return item.alt;
  }

  const src = item?.asset?.url ?? "";
  if (src.includes("cta-gif")) return "TMRW contact illustration";
  if (src.includes("hero-gif")) return "TMRW performance marketing illustration";
  return isMainHero ? "TMRW performance marketing illustration" : "TMRW visual icon";
}

export default function HeroSection({ data, isHero, title }: Props) {
  const { openLightbox, trackCalendlyClick } = useAppContext();
  const t = useTranslations("form");
  const buttons: HeroButton[] = data?.buttons ?? defaultButtons;

  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
  });

  const headlineArray =
    data?.headline?.flatMap((item: any) => {
      if (item._type === "copy") {
        return item.text?.split(" ");
      } else {
        return item;
      }
    }) ?? [];

  const Tag = isHero ? "h1" : "h2";
  return (
    <m.div
      ref={ref}
      variants={variantsParent}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
      className={`${data?.backgroundColor === "black" ? "bg-neutral-900 text-white" : data?.backgroundColor === "white" ? "bg-neutral-200" : "bg-primary"} ${isHero ? "pt-32 pb-16 md:pt-40 md:pb-24 min-h-[66vh]" : "section"} flex flex-col justify-center items-center gap-8 px-4`}
    >
      {headlineArray.length > 0 && (
        <Tag className="text-center h1 uppercase flex flex-wrap justify-center items-center max-w-7xl">
          {headlineArray.map((item: any, index: number) => {
            if (item._type === "icon") {
              const hasCustomAlt = typeof item.alt === "string" && item.alt.trim().length > 0;
              return (
                <div key={index} className="overflow-hidden flex items-center">
                  <m.div variants={variantsText} className="flex">
                    <Image
                      className="w-[7vw] h-[7vw] lg:w-20 lg:h-20"
                      src={item.asset.url}
                      width={200}
                      height={200}
                      alt={getHeadlineIconAlt(item, isHero)}
                      aria-hidden={hasCustomAlt ? undefined : true}
                      sizes="(min-width: 1024px) 80px, 7vw"
                      priority={isHero && index === 1}
                      unoptimized
                    />
                    &nbsp;
                  </m.div>
                </div>
              );
            } else {
              return (
                <span
                  key={index}
                  className="overflow-hidden"
                  style={{ perspective: 2000 }}
                >
                  <m.span
                    variants={variantsText}
                    className="inline-block pb-1 sm:pb-2"
                  >
                    {item}&nbsp;
                  </m.span>
                </span>
              );
            }
          })}
        </Tag>
      )}
      {data?.text && (
        <m.p
          variants={variantsBlock}
          className="text-center text-xl max-w-2xl overflow-hidden"
        >
          {data?.text}
        </m.p>
      )}
      {(data?.cta || data?.buttons) && (
        <m.div
          variants={variantsBlock}
          className="grid justify-center md:flex items-center gap-2 overflow-hidden"
        >
          {buttons.map((btn: HeroButton, index: number) => {
            if (btn.type === "calendly") {
              return (
                <div key={index} className="w-full md:w-auto">
                  <CalendlyButton
                    url="https://calendly.com/tmrw-marketing"
                    variant={(btn.variant as ButtonVariant) || "outline"}
                    className="w-full justify-center"
                    onOpen={trackCalendlyClick}
                  >
                    {t("calendlyButton")}
                  </CalendlyButton>
                </div>
              );
            }
            return (
              <Button
                key={index}
                variant={(btn.variant as ButtonVariant) || "default"}
                onClick={() => openLightbox(btn.action)}
              >
                {t(btn.label)}
              </Button>
            );
          })}
        </m.div>
      )}
    </m.div>
  );
}
